import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { UserPlus, Key, Edit, Trash2, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useManuals } from '../../contexts/ManualsContext';
import { apiGetUsers, apiCreateUser } from '../../utils/api';
import { getAuthToken } from '../../utils/auth';
import { ResetPasswordModal } from './ResetPasswordModal';
import { EditUserModal } from './EditUserModal';
import { DeleteUserConfirmation } from './DeleteUserConfirmation';
import { GeneratedPasswordModal } from './GeneratedPasswordModal';

export const UserManagementModal = () => {
  const { darkMode } = useTheme();
  const { userRole } = useAuth();
  const { isManagingUsers, setIsManagingUsers } = useManuals();
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ username: '', name: '', email: '', role: 'user', password: '' });
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [userToReset, setUserToReset] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [newlyGeneratedPassword, setNewlyGeneratedPassword] = useState('');
  const [showGeneratedModal, setShowGeneratedModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [expandedUsers, setExpandedUsers] = useState([]); // для мобильных карточек

  useEffect(() => {
    if (isManagingUsers && userRole === 'admin') {
      loadUsers();
    }
  }, [isManagingUsers, userRole]);

  const loadUsers = async () => {
    const token = getAuthToken();
    const data = await apiGetUsers(token);
    if (data.success) setUsers(data.users);
  };

  const handleCreateUser = async () => {
    if (!newUser.username.trim() || !newUser.name.trim() || !newUser.email.trim()) {
      alert('Заполните все поля');
      return;
    }
    const token = getAuthToken();
    const data = await apiCreateUser(token, newUser);
    if (data.success) {
      if (data.password) {
        setGeneratedPassword(data.password);
        setShowGeneratedModal(true);
      } else {
        alert('Пользователь успешно создан!');
        setNewUser({ username: '', name: '', email: '', role: 'user', password: '' });
        loadUsers();
      }
    } else {
      alert(`Ошибка: ${data.error}`);
    }
  };

  const toggleUserExpand = (userId) => {
    setExpandedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  return (
    <>
      {isManagingUsers && userRole === 'admin' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setIsManagingUsers(false)}
        >
          <div
            className={`rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto p-4 md:p-6 ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Управление пользователями
              </h2>
              <button
                onClick={() => setIsManagingUsers(false)}
                className={`p-2 rounded-lg ${
                  darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Форма создания нового пользователя */}
            <div className={`mb-6 p-4 rounded-lg ${
              darkMode ? 'bg-blue-900/30 border border-blue-800' : 'bg-blue-50'
            }`}>
              <h3 className={`font-bold text-lg mb-3 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                Создать нового пользователя
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  placeholder="Логин *"
                  className={`px-3 py-2 text-sm rounded-lg ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                  }`}
                />
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  placeholder="Имя *"
                  className={`px-3 py-2 text-sm rounded-lg ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                  }`}
                />
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  placeholder="Email *"
                  className={`px-3 py-2 text-sm rounded-lg ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                  }`}
                />
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className={`px-3 py-2 text-sm rounded-lg ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                  }`}
                >
                  <option value="user">Пользователь</option>
                  <option value="admin">Администратор</option>
                </select>
                <div className="md:col-span-2">
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    placeholder="Пароль (оставьте пустым для генерации)"
                    className={`w-full px-3 py-2 text-sm rounded-lg ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                    }`}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={handleCreateUser}
                  className={`flex items-center px-4 py-2 rounded-lg text-sm ${
                    darkMode ? 'bg-green-700 hover:bg-green-600 text-white' : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Создать
                </button>
              </div>
            </div>

            {/* Список пользователей */}
            <div>
              <h3 className={`font-bold text-lg mb-3 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                Пользователи ({users.length})
              </h3>

              {/* Десктопная таблица — скрывается на мобильном */}
              <div className="hidden md:block overflow-x-auto">
                <table className={`w-full text-sm ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <tr>
                      <th className="p-2 text-left">Имя</th>
                      <th className="p-2 text-left">Логин</th>
                      <th className="p-2 text-left">Email</th>
                      <th className="p-2 text-left">Роль</th>
                      <th className="p-2 text-left">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.id} className="border-t border-gray-200 dark:border-gray-700">
                        <td className="p-2 font-medium">{user.name}</td>
                        <td className="p-2 text-sm">{user.username}</td>
                        <td className="p-2 text-sm">{user.email}</td>
                        <td className="p-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            user.role === 'admin'
                              ? (darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800')
                              : (darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800')
                          }`}>
                            {user.role === 'admin' ? 'Админ' : 'Пользователь'}
                          </span>
                        </td>
                        <td className="p-2">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                setUserToEdit(user);
                                setShowEditModal(true);
                                setIsManagingUsers(false);
                              }}
                              className={`flex items-center text-xs px-2 py-1 rounded ${
                                darkMode ? 'text-blue-400 hover:bg-gray-700' : 'text-blue-600 hover:bg-blue-50'
                              }`}
                              title="Редактировать"
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Изменить
                            </button>
                            <button
                              onClick={() => {
                                setUserToReset(user);
                                setShowResetModal(true);
                                setIsManagingUsers(false);
                              }}
                              className={`flex items-center text-xs px-2 py-1 rounded ${
                                darkMode ? 'text-blue-400 hover:bg-gray-700' : 'text-blue-600 hover:bg-blue-50'
                              }`}
                              title="Сбросить пароль"
                            >
                              <Key className="w-3 h-3 mr-1" />
                              Сбросить
                            </button>
                            <button
                              onClick={() => {
                                setUserToDelete(user);
                                setShowDeleteConfirmation(true);
                                setIsManagingUsers(false);
                              }}
                              className={`flex items-center text-xs px-2 py-1 rounded ${
                                darkMode ? 'text-red-400 hover:bg-gray-700' : 'text-red-600 hover:bg-red-50'
                              }`}
                              title="Удалить"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Удалить
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Мобильные карточки — показываются на md и ниже */}
              <div className="md:hidden space-y-3">
                {users.map(user => (
                  <div
                    key={user.id}
                    className={`p-3 rounded-lg border ${
                      darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div
                      className="flex justify-between items-center cursor-pointer"
                      onClick={() => toggleUserExpand(user.id)}
                    >
                      <div>
                        <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>{user.name}</p>
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>@{user.username}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.role === 'admin'
                            ? (darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800')
                            : (darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800')
                        }`}>
                          {user.role === 'admin' ? 'Админ' : 'Пользователь'}
                        </span>
                        {expandedUsers.includes(user.id) ? (
                          <ChevronUp className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        )}
                      </div>
                    </div>

                    {expandedUsers.includes(user.id) && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <p className={`text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <span className="font-medium">Email:</span> {user.email}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => {
                              setUserToEdit(user);
                              setShowEditModal(true);
                              setIsManagingUsers(false);
                            }}
                            className={`flex items-center text-xs px-3 py-2 rounded ${
                              darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'
                            }`}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Изменить
                          </button>
                          <button
                            onClick={() => {
                              setUserToReset(user);
                              setShowResetModal(true);
                              setIsManagingUsers(false);
                            }}
                            className={`flex items-center text-xs px-3 py-2 rounded ${
                              darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'
                            }`}
                          >
                            <Key className="w-3 h-3 mr-1" />
                            Сбросить пароль
                          </button>
                          <button
                            onClick={() => {
                              setUserToDelete(user);
                              setShowDeleteConfirmation(true);
                              setIsManagingUsers(false);
                            }}
                            className={`flex items-center text-xs px-3 py-2 rounded ${
                              darkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-700'
                            }`}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Удалить
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Модальные окна */}
      {showResetModal && userToReset && (
        <ResetPasswordModal
          user={userToReset}
          onClose={() => {
            setShowResetModal(false);
            setUserToReset(null);
            setIsManagingUsers(true);
          }}
          onPasswordGenerated={(pwd) => {
            setNewlyGeneratedPassword(pwd);
            setShowGeneratedModal(true);
          }}
        />
      )}

      {showGeneratedModal && (
        <GeneratedPasswordModal
          username={userToReset?.username || newUser.username}
          password={generatedPassword || newlyGeneratedPassword}
          onClose={() => {
            setShowGeneratedModal(false);
            setGeneratedPassword('');
            setNewlyGeneratedPassword('');
            setNewUser({ username: '', name: '', email: '', role: 'user', password: '' });
            loadUsers();
          }}
        />
      )}

      {showEditModal && userToEdit && (
        <EditUserModal user={userToEdit} onClose={() => {
          setShowEditModal(false);
          setUserToEdit(null);
          setIsManagingUsers(true);
          loadUsers();
        }} />
      )}

      {showDeleteConfirmation && userToDelete && (
        <DeleteUserConfirmation
          user={userToDelete}
          onClose={() => {
            setShowDeleteConfirmation(false);
            setUserToDelete(null);
            setIsManagingUsers(true);
            loadUsers();
          }}
        />
      )}
    </>
  );
};